import { useState, Fragment } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Check, ArrowRight, Sparkles, Plus, Minus,
  Coins, Store, Plug, Building2, GraduationCap, Wrench,
} from "lucide-react";
import { MarketingNav, MarketingFooter } from "@/components/marketing/MarketingChrome";
import { ContactSalesDialog } from "@/components/marketing/ContactSalesDialog";
import { Seo } from "@/components/Seo";
import { Button } from "@/components/ui/button";
import {
  TIERS, ADDONS, FAQS, priceLabel,
  FEATURE_GROUPS, COMPARE_COLUMNS, COMPARE_HIGHLIGHT,
} from "@/data/pricing";

const ICONS = { Coins, Store, Plug, Building2, GraduationCap, Wrench };

function Cell({ value }) {
  if (value === true)
    return <Check size={16} className="mx-auto text-[hsl(var(--matcha))]" />;
  if (value === false || value == null)
    return <Minus size={14} className="mx-auto text-muted-foreground/40" />;
  return <span className="text-xs text-foreground">{value}</span>;
}

function ComparisonTable() {
  const tierByKey = Object.fromEntries(TIERS.map((t) => [t.key, t]));
  const isHot = (key) => COMPARE_HIGHLIGHT.includes(key);
  return (
    <section className="border-y border-border bg-background" data-testid="pricing-compare">
      <div className="mx-auto max-w-6xl px-6 py-16">
        <div className="mb-8">
          <div className="font-mono text-[11px] uppercase tracking-[0.3em] text-[hsl(var(--teal))]">Full breakdown</div>
          <h2 className="mt-3 font-display text-3xl font-semibold tracking-tight sm:text-4xl">Compare all features</h2>
          <p className="mt-2 max-w-2xl text-muted-foreground">
            Everything each plan includes. <span className="text-foreground">Professional</span> and{" "}
            <span className="text-foreground">Business</span> unlock the research, collaboration and scale most teams need.
          </p>
        </div>

        <div className="overflow-x-auto rounded-md border border-border">
          <table className="w-full min-w-[820px] border-collapse text-sm" data-testid="compare-table">
            <thead>
              <tr className="border-b border-border bg-card">
                <th className="sticky left-0 z-10 bg-card px-4 py-4 text-left align-bottom">
                  <span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Feature</span>
                </th>
                {COMPARE_COLUMNS.map((key) => {
                  const t = tierByKey[key];
                  const p = priceLabel(t, "monthly");
                  return (
                    <th
                      key={key}
                      data-testid={`compare-col-${key}`}
                      className={`px-3 py-4 text-center align-bottom ${
                        isHot(key) ? "bg-[hsl(var(--matcha)/0.08)]" : ""
                      }`}
                    >
                      <div className={`font-display text-sm font-semibold tracking-tight ${isHot(key) ? "text-[hsl(var(--teal))]" : ""}`}>
                        {t.name}
                      </div>
                      <div className="mt-0.5 font-mono text-[11px] text-muted-foreground">
                        {p.amount}{p.sub}
                      </div>
                      {t.popular && (
                        <div className="mt-1 inline-block rounded-sm bg-[hsl(var(--matcha))] px-1.5 py-0.5 font-mono text-[9px] uppercase tracking-widest text-[hsl(var(--matcha-foreground))]">
                          Popular
                        </div>
                      )}
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {FEATURE_GROUPS.map((grp) => (
                <Fragment key={grp.group}>
                  <tr className="bg-secondary/40">
                    <td
                      colSpan={COMPARE_COLUMNS.length + 1}
                      className="px-4 py-2.5 font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground"
                    >
                      {grp.group}
                    </td>
                  </tr>
                  {grp.rows.map((row, ri) => (
                    <tr key={`${grp.group}-${ri}`} className="border-b border-border/50 last:border-0">
                      <td className="sticky left-0 z-10 bg-background px-4 py-3 text-left text-foreground">{row.label}</td>
                      {COMPARE_COLUMNS.map((key) => (
                        <td
                          key={key}
                          className={`px-3 py-3 text-center ${isHot(key) ? "bg-[hsl(var(--matcha)/0.05)]" : ""}`}
                        >
                          <Cell value={row[key]} />
                        </td>
                      ))}
                    </tr>
                  ))}
                </Fragment>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </section>
  );
}

function TierCard({ tier, period, onContact }) {
  const p = priceLabel(tier, period);
  const variant = tier.popular ? "default" : "outline";
  const label = (
    <>
      {tier.cta} <ArrowRight size={15} className="ml-1.5" />
    </>
  );
  // Paid subscription tiers route into the billing checkout; free → register, enterprise → contact.
  const isPaidSub = !tier.contact && tier.key !== "free";
  const to = isPaidSub ? `/billing?plan=${tier.key}&period=${period}` : tier.to;
  return (
    <div
      data-testid={`pricing-tier-${tier.key}`}
      className={`relative flex flex-col rounded-md border bg-card p-6 transition-colors ${
        tier.popular ? "border-primary shadow-[0_0_0_1px_hsl(var(--primary))]" : "border-border hover:border-[hsl(var(--teal))]/50"
      }`}
    >
      {tier.popular && (
        <span className="absolute -top-3 left-6 rounded-sm bg-[hsl(var(--matcha))] px-2.5 py-1 font-mono text-[10px] uppercase tracking-widest text-[hsl(var(--matcha-foreground))]">
          Most popular
        </span>
      )}
      <div className="font-mono text-[11px] uppercase tracking-[0.25em] text-muted-foreground">{tier.name}</div>
      <div className="mt-3 flex items-end gap-1">
        <span className="font-display text-4xl font-bold tracking-tight">{p.amount}</span>
        {p.sub && <span className="mb-1 font-mono text-sm text-muted-foreground">{p.sub}</span>}
      </div>
      <div className="mt-1 h-4 font-mono text-[11px] text-primary">{p.note || ""}</div>
      <div className="mt-1 text-sm text-muted-foreground">{tier.who}</div>

      <ul className="mt-6 flex-1 space-y-2.5">
        {tier.features.map((f) => (
          <li key={f} className="flex items-start gap-2 text-sm">
            <Check size={16} className="mt-0.5 shrink-0 text-[hsl(var(--processor-success))]" />
            <span>{f}</span>
          </li>
        ))}
      </ul>

      <div className="mt-7">
        {tier.contact ? (
          <Button variant={variant} className="w-full rounded-sm" onClick={onContact} data-testid={`pricing-cta-${tier.key}`}>
            {label}
          </Button>
        ) : (
          <Link to={to} data-testid={`pricing-cta-${tier.key}`}>
            <Button variant={variant} className="w-full rounded-sm">{label}</Button>
          </Link>
        )}
      </div>
    </div>
  );
}

function FaqItem({ item, index }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-border" data-testid={`faq-item-${index}`}>
      <button
        onClick={() => setOpen((o) => !o)}
        className="flex w-full items-center justify-between gap-4 py-5 text-left"
        data-testid={`faq-toggle-${index}`}
      >
        <span className="font-display text-lg font-medium tracking-tight">{item.q}</span>
        {open ? <Minus size={18} className="shrink-0 text-primary" /> : <Plus size={18} className="shrink-0 text-muted-foreground" />}
      </button>
      {open && <p className="pb-5 pr-8 leading-relaxed text-muted-foreground">{item.a}</p>}
    </div>
  );
}

export default function Pricing() {
  const [period, setPeriod] = useState("monthly");
  const [contactOpen, setContactOpen] = useState(false);

  return (
    <div className="min-h-screen bg-background" data-testid="pricing-page">
      <Seo
        title="Pricing — Espresso Knowledge Processor"
        description="Simple pricing for every scale of knowledge. Start free, scale to enterprise. Free, Student $5, Creator $15, Professional $29, Business $79, and custom Enterprise plans."
        path="/pricing"
      />
      <MarketingNav />
      <ContactSalesDialog open={contactOpen} onOpenChange={setContactOpen} />

      {/* Hero */}
      <section className="border-b border-border grid-lines">
        <div className="mx-auto max-w-6xl px-6 py-16 lg:py-20">
          <div className="mb-8 flex items-center gap-4 font-mono text-[11px] uppercase tracking-[0.3em] text-muted-foreground">
            <span className="text-primary">Pricing</span>
            <span className="h-px flex-1 bg-border" />
            <span>From Knowledge to Intelligence</span>
          </div>
          <motion.h1
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-4xl font-display text-4xl font-bold leading-[1.05] tracking-tight sm:text-5xl lg:text-6xl"
            data-testid="pricing-title"
          >
            Simple pricing for every <span className="text-primary">scale of knowledge</span>.
          </motion.h1>
          <p className="mt-6 max-w-2xl text-lg leading-relaxed text-muted-foreground">
            Research. Structure. Understand. Build. Start free, grow as your knowledge does, and scale
            all the way to private enterprise deployments.
          </p>

          {/* Billing toggle */}
          <div className="mt-8 inline-flex items-center gap-1 rounded-md border border-border bg-card p-1" data-testid="billing-toggle">
            <button
              onClick={() => setPeriod("monthly")}
              data-testid="toggle-monthly"
              className={`rounded-sm px-4 py-1.5 font-mono text-xs uppercase tracking-wider transition-colors ${
                period === "monthly" ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"
              }`}
            >
              Monthly
            </button>
            <button
              onClick={() => setPeriod("annual")}
              data-testid="toggle-annual"
              className={`flex items-center gap-2 rounded-sm px-4 py-1.5 font-mono text-xs uppercase tracking-wider transition-colors ${
                period === "annual" ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"
              }`}
            >
              Annual
              <span className={`rounded-sm px-1.5 py-0.5 text-[10px] ${period === "annual" ? "bg-primary-foreground/20" : "bg-[hsl(var(--processor-success))]/15 text-[hsl(var(--processor-success))]"}`}>
                2 months free
              </span>
            </button>
          </div>
        </div>
      </section>

      {/* Tiers */}
      <section className="mx-auto max-w-6xl px-6 py-16">
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {TIERS.map((t) => (
            <TierCard key={t.key} tier={t} period={period} onContact={() => setContactOpen(true)} />
          ))}
        </div>
        <p className="mt-6 text-center font-mono text-[11px] uppercase tracking-widest text-muted-foreground">
          Prices in USD · cancel anytime · all plans include the full processor pipeline
        </p>
      </section>

      {/* Compare all features */}
      <ComparisonTable />

      {/* Add-ons */}
      <section className="border-y border-border bg-secondary/30" data-testid="pricing-addons">
        <div className="mx-auto max-w-6xl px-6 py-16">
          <div className="mb-10">
            <div className="font-mono text-[11px] uppercase tracking-[0.3em] text-primary">Beyond the plans</div>
            <h2 className="mt-3 font-display text-3xl font-semibold tracking-tight sm:text-4xl">Add-ons &amp; revenue streams</h2>
            <p className="mt-2 max-w-2xl text-muted-foreground">Scale usage, publish and sell your knowledge, or have us engineer it for you.</p>
          </div>
          <div className="grid grid-cols-1 gap-px overflow-hidden rounded-md border border-border bg-border sm:grid-cols-2 lg:grid-cols-3">
            {ADDONS.map(({ icon, title, desc }) => {
              const Icon = ICONS[icon];
              return (
                <div key={title} className="bg-card p-6">
                  {Icon && <Icon size={20} className="text-primary" />}
                  <h3 className="mt-4 font-display text-lg font-semibold tracking-tight">{title}</h3>
                  <p className="mt-1.5 text-sm leading-relaxed text-muted-foreground">{desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="mx-auto max-w-3xl px-6 py-16" data-testid="pricing-faq">
        <div className="mb-6">
          <div className="font-mono text-[11px] uppercase tracking-[0.3em] text-primary">Questions</div>
          <h2 className="mt-3 font-display text-3xl font-semibold tracking-tight sm:text-4xl">Frequently asked</h2>
        </div>
        <div className="border-t border-border">
          {FAQS.map((item, i) => (
            <FaqItem key={i} item={item} index={i} />
          ))}
        </div>
      </section>

      {/* Final CTA */}
      <section className="border-t border-border grid-lines">
        <div className="mx-auto flex max-w-6xl flex-col items-start justify-between gap-6 px-6 py-16 sm:flex-row sm:items-center">
          <div>
            <div className="font-mono text-[11px] uppercase tracking-[0.3em] text-primary">Build what you know</div>
            <h2 className="mt-3 font-display text-3xl font-semibold tracking-tight sm:text-4xl">Start processing knowledge today.</h2>
          </div>
          <div className="flex gap-3">
            <Button variant="outline" className="rounded-sm" onClick={() => setContactOpen(true)} data-testid="pricing-contact-sales">
              Talk to sales
            </Button>
            <Link to="/register">
              <Button className="rounded-sm px-8" data-testid="pricing-begin">
                <Sparkles size={16} className="mr-2" /> Begin free
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <MarketingFooter />
    </div>
  );
}
